import { isEqual } from 'lodash';
import { useEffect } from 'react';

import {
  type CustomSkillTaxonomyId,
  getAdaptiveSkillTaxonomyValue,
} from '@revelio/iso-utility';

import { useSelectionListsForKeys } from '../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { useCurrentSkillTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useSkillTaxonomyAdapter';
import type { SelectionList } from '../../../../shared/selection-lists/types';
import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../shared/user-preferences/types';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import type { RevelioPageId } from '../../components/PageConfigProvider/types';
import { DEFAULT_SKILL_TAXONOMY_ID } from '../contexts/defaultFilters';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import {
  type FilterConsistencyState,
  NO_PRIMARY_FILTER_KEY,
} from '../engine/filterGroupConsistency';
import type { FilterAction } from '../engine/filterReducer';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';

export { DEFAULT_SKILL_TAXONOMY_ID };

export const useAdaptiveSkillTaxonomy = ({
  primaryFilterId,
  revelioPageId,
  filterState,
  dispatch,
}: {
  primaryFilterId: PrimaryFilterId | undefined;
  revelioPageId?: RevelioPageId;
  filterState: FilterConsistencyState;
  dispatch: React.Dispatch<FilterAction>;
}) => {
  // rics_k400 carries the k10/k50 ancestry FKs the adaptive resolver maps over.
  const { selectionLists } = useSelectionListsForKeys(['rics_k400']);
  const ricsK400List = selectionLists.rics_k400 ?? null;
  const { isValidTaxonomyId, loading: loadingTaxonomyVersions } =
    useCurrentSkillTaxonomyVersion();

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    // During navigation between page groups, the provider can briefly render with a
    // new `primaryFilterId` prop while the reducer state still points at the previous
    // `activeKey`. Avoid dispatching updates into the wrong group map.
    if (primaryFilterId && filterState.activeKey !== primaryFilterId) {
      return;
    }

    const activeFilterMap = filterState.groupMap.get(
      primaryFilterId ?? NO_PRIMARY_FILTER_KEY
    );
    const companyFilterState = activeFilterMap?.get(FilterId.COMPANY);
    const skillFilterState = activeFilterMap?.get(FilterId.CUSTOM_SKILL);
    const skillTaxonomyFilterState = activeFilterMap?.get(
      FilterId.SKILL_TAXONOMY
    ) as SingleValueFilter<CustomSkillTaxonomySelection>;

    const allStatesSet =
      !!companyFilterState &&
      companyFilterState.type === FilterType.TREE &&
      !!skillFilterState &&
      !!skillTaxonomyFilterState &&
      skillTaxonomyFilterState.type === FilterType.VALUE;
    if (!allStatesSet) {
      return;
    }

    // If the user has an explicit Company taxonomy preference (non-adaptive),
    // treat it like a user-set taxonomy and never override it via adaptive logic.
    if (primaryFilterId === FilterId.COMPANY) {
      const companyPrefs = PreferenceBrowserStorage.get(
        PreferenceScope.COMPANY
      );
      const skillPref = companyPrefs[UserPreferenceKey.Taxonomies]?.skill;

      if (typeof skillPref === 'number') {
        const preferenceValue: CustomSkillTaxonomySelection = {
          taxonomyId: skillPref,
          version: skillTaxonomyFilterState.value.version,
        };

        if (
          skillTaxonomyFilterState.isUserSubmitted !== true ||
          !isEqual(skillTaxonomyFilterState.value.taxonomyId, skillPref)
        ) {
          dispatch({
            type: 'UPDATE_FILTER',
            filter: {
              ...skillTaxonomyFilterState,
              value: preferenceValue,
              isUserSubmitted: true,
            },
          });
        }
        return;
      }
    }

    // If a user explicitly selected a specific taxonomy (not default/adaptive),
    // do not auto-update via adaptive logic.
    //
    // Only trust `isUserSubmitted` on non-COMPANY pages. On COMPANY pages we
    // already handled a concrete saved/session preference above, so if we
    // reach here the user's effective preference is adaptive (or unset) —
    // any `isUserSubmitted: true` is almost certainly stale from another
    // scope's preference sync (e.g. a ROLE page set it to
    // `{ taxonomyId: 0, isUserSubmitted: true }` and that state is leaking
    // into this COMPANY page). Letting the adaptive logic below run fixes
    // the flag and dispatches the right value for the current company
    // selection.
    const userSetTaxonomy = !!skillTaxonomyFilterState?.isUserSubmitted;
    if (userSetTaxonomy && primaryFilterId !== FilterId.COMPANY) {
      return;
    }

    const adaptiveNotSupportedOnPage = primaryFilterId !== FilterId.COMPANY;

    const defaultTaxonomy: CustomSkillTaxonomySelection = {
      taxonomyId: DEFAULT_SKILL_TAXONOMY_ID,
      version: skillTaxonomyFilterState.value.version,
    };

    if (adaptiveNotSupportedOnPage) {
      if (!isEqual(skillTaxonomyFilterState.value, defaultTaxonomy)) {
        dispatch({
          type: 'UPDATE_FILTER',
          filter: {
            ...skillTaxonomyFilterState,
            value: defaultTaxonomy,
          },
        });
      }
      return;
    }

    // Don't update while taxonomy versions are still loading
    if (loadingTaxonomyVersions) {
      return;
    }

    // Wait for the RICS tree before computing an adaptive taxonomy. Otherwise
    // we'd dispatch a default while it loads and clear custom-skill state under
    // the wrong taxonomy (mirrors useAdaptiveRoleTaxonomy's guard).
    if (!ricsK400List?.length) {
      return;
    }

    const skillTaxonomyValue = getSkillTaxonomyValue(
      companyFilterState,
      ricsK400List
    );

    // Validate the computed taxonomy ID against available taxonomies
    const computedId =
      skillTaxonomyValue.taxonomyId ?? DEFAULT_SKILL_TAXONOMY_ID;
    const isValid = isValidTaxonomyId(computedId);
    const resolvedSkillTaxonomyValue = isValid
      ? skillTaxonomyValue
      : defaultTaxonomy;

    // Avoid auto-updating taxonomy when custom skill selections exist, since
    // taxonomy changes clear custom skill selections via dependencies. Still
    // clear a stale `isUserSubmitted` (e.g. carried in from a deeplink) so the
    // unchanged value reads as Adaptive — we're past the concrete-preference
    // early-return, so the effective mode is adaptive.
    if (
      skillFilterState.type === FilterType.TREE &&
      skillFilterState.selections.length > 0
    ) {
      if (skillTaxonomyFilterState.isUserSubmitted === true) {
        dispatch({
          type: 'UPDATE_FILTER',
          filter: { ...skillTaxonomyFilterState, isUserSubmitted: false },
        });
      }
      return;
    }

    // Reset a stale `isUserSubmitted` even when the value is unchanged (e.g. a
    // deeplink restored the adaptive value flagged user-submitted): the value
    // matches, so without this the flag would never clear and the taxonomy
    // would wrongly read as concrete.
    if (
      !isEqual(
        resolvedSkillTaxonomyValue.taxonomyId,
        skillTaxonomyFilterState.value.taxonomyId
      ) ||
      skillTaxonomyFilterState.isUserSubmitted === true
    ) {
      dispatch({
        type: 'UPDATE_FILTER',
        filter: {
          ...skillTaxonomyFilterState,
          value: {
            ...resolvedSkillTaxonomyValue,
            version: skillTaxonomyFilterState.value.version,
          },
          isUserSubmitted: false,
        },
      });
    }
  }, [
    primaryFilterId,
    revelioPageId,
    filterState,
    dispatch,
    ricsK400List,
    isValidTaxonomyId,
    loadingTaxonomyVersions,
  ]);
};

// Lightweight wrapper that extracts the relevant selections from the company filter
// and delegates the heavy-lifting to `getAdaptiveSkillTaxonomyValue`.
export function getSkillTaxonomyValue(
  companyFilter: TreeFilter,
  ricsK400List: SelectionList<'rics_k400'>
): CustomSkillTaxonomySelection {
  const ricsK400Ids = companyFilter.selections
    // Include `rics_k400` industry selections so a k400 industry adapts to its
    // k50 parent taxonomy (mirrors getRoleTaxonomyValue; keeps role + skill
    // taxonomies on the same RICS level for the same industry selection).
    .filter(
      ({ levelId }) =>
        levelId === 'company' ||
        levelId === 'rics_k50' ||
        levelId === 'rics_k400'
    )
    .map((selection) => selection.id.split('_')?.[1] ?? null)
    .filter((d): d is string => d !== null)
    .filter((d) => Number(d) <= 400);

  const ricsK10Ids = companyFilter.selections
    .map((selection) => selection.id.split('_')?.[0] ?? null)
    .filter((d): d is string => d !== null)
    .filter((d) => Number(d) <= 10);

  const ricsK50Ids = companyFilter.selections
    .filter(({ levelId }) => levelId === 'rics_k50')
    .map((selection) => selection.id.split('_')?.[1] ?? selection.id)
    .filter((d): d is string => d !== null)
    .filter((d) => Number(d) <= 50);

  const taxonomyId = getAdaptiveSkillTaxonomyValue({
    ricsK400List,
    ricsSelections: {
      rics_k10: ricsK10Ids,
      rics_k50: ricsK50Ids,
      rics_k400: ricsK400Ids,
    },
  });

  return getTaxonomySelection(taxonomyId);
}

export const getTaxonomySelection = (
  taxonomyId: CustomSkillTaxonomyId
): CustomSkillTaxonomySelection => {
  if (!taxonomyId || taxonomyId.includes('default')) {
    return { taxonomyId: DEFAULT_SKILL_TAXONOMY_ID };
  }

  if (taxonomyId.includes('rics_k10')) {
    const id = taxonomyId.split('custom_skill_rics_k10_')[1];
    return id
      ? // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
        { taxonomyId: 50 + parseInt(id) }
      : { taxonomyId: DEFAULT_SKILL_TAXONOMY_ID };
  }

  if (taxonomyId.includes('rics_k50')) {
    const id = taxonomyId.split('custom_skill_rics_k50_')[1];
    return id
      ? // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
        { taxonomyId: parseInt(id) }
      : { taxonomyId: DEFAULT_SKILL_TAXONOMY_ID };
  }
  return { taxonomyId: DEFAULT_SKILL_TAXONOMY_ID };
};
